/**
 * ProfileContact — SMS adapter for the shared `@tiaki/profile-ui`
 * `ProfileContactSection` (ADR-141 §5).
 *
 * The SMS mirror of AIM's `ProfileContactAdapter`: owns the
 * `/api/v2/me/contact*` data + mutations and the toaster; the shared
 * section stays presentational. Zero-surface when the org's
 * MASS_NOTIFICATION_CONTACTS flag is off (ADR-097 pattern). Email adds
 * auto-kick a verification message (§6); phone verification stays
 * admin-driven in v1.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProfileContactSection } from '@tiaki/profile-ui';
import type { ProfileContactDestination } from '@tiaki/profile-ui';
import { useFeature } from '../../hooks/useFeature';
import {
  addMyContactDestination,
  getMyContact,
  removeMyContactDestination,
  sendDestinationVerification,
  updateMyContactDestination,
} from '../../services/contact.service';
import { FeatureCode } from '../../types/features';
import { toaster } from '../ui/toaster';

const QUERY_KEY = ['me-contact'];

// The drawer passes the fetched user's cell_phone so the section
// can surface it as the implicit, non-removable account-phone row.
export const ProfileContact = ({
  personalPhone = null,
}: {
  personalPhone?: string | null;
}) => {
  const { t } = useTranslation();
  const enabled = useFeature(FeatureCode.MASS_NOTIFICATION_CONTACTS);
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getMyContact,
    enabled,
  });

  if (!enabled) return null;

  const destinations = ((
    data as { destinations?: ProfileContactDestination[] } | undefined
  )?.destinations ?? []) as ProfileContactDestination[];

  const run = async (fn: () => Promise<unknown>, successTitle: string) => {
    setIsSaving(true);
    try {
      await fn();
      if (successTitle)
        toaster.create({
          type: 'success',
          duration: 3000,
          title: successTitle,
        });
    } catch (error: unknown) {
      const detail =
        typeof error === 'object' && error !== null && 'address' in error
          ? String((error as { address: unknown }).address)
          : typeof error === 'object' && error !== null && 'detail' in error
            ? String((error as { detail: unknown }).detail)
            : t('common.somethingWentWrong', 'Something went wrong.');
      toaster.create({
        type: 'error',
        duration: 5000,
        title: t(
          'profile.couldNotUpdateDestinations',
          'Could not update destinations',
        ),
        description: detail,
      });
    } finally {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setIsSaving(false);
    }
  };

  return (
    <ProfileContactSection
      destinations={destinations}
      personalPhone={personalPhone}
      isSaving={isSaving}
      onAdd={(channelCode, address) =>
        run(
          async () => {
            await addMyContactDestination(channelCode, address);
            if (channelCode === 'mail') {
              const refreshed = (await getMyContact()) as {
                destinations?: ProfileContactDestination[];
              };
              const added = (refreshed.destinations ?? []).find(
                (d) => d.channel_code === channelCode && !d.verified_at,
              );
              if (added) await sendDestinationVerification(added.id);
            }
          },
          t(
            'profile.destinationAddedCheckVerification',
            'Destination added — check for a verification message',
          ),
        )
      }
      onToggle={(id, nextEnabled) =>
        run(() => updateMyContactDestination(id, { enabled: nextEnabled }), '')
      }
      onRemove={(id) =>
        run(
          () => removeMyContactDestination(id),
          t('profile.destinationRemoved', 'Destination removed'),
        )
      }
      onResendVerification={(id) =>
        run(
          () => sendDestinationVerification(id),
          t('profile.verificationSent', 'Verification sent'),
        )
      }
      e2eTitle="profileContactSection"
    />
  );
};

export default ProfileContact;
